<script setup lang="ts">
import { computed, type ComputedRef, onMounted, ref, watch } from 'vue';
import { VueDraggableNext } from 'vue-draggable-next';
import type { ModuleIdNameMapping } from '@/models/Robot';
import type { ModuleFolderMapping } from '@/models/RobotFinder';
import gettext from '@/i18n/gettext';

interface Props {
  modules: ModuleIdNameMapping[];
  showClearAll?: boolean;
  moduleFolderMapping: ModuleFolderMapping;
  validateConfig?: boolean;
  validConfigurations?: string[];
}

interface Emits {
  (e: 'update:modules', modules: ModuleIdNameMapping[]): void;
  (e: 'remove', index: number): void;
  (e: 'clear'): void;
  (e: 'validation-error', error: string | null): void;
}

const props = withDefaults(defineProps<Props>(), {
  showClearAll: true,
  validateConfig: true,
  validConfigurations: () => [],
});

const emit = defineEmits<Emits>();

const { $gettext } = gettext;

const editableModules = ref<ModuleIdNameMapping[]>([...props.modules]);

const validationError: ComputedRef<string | null> = computed(() => {
  if (
    !props.validateConfig ||
    !props.moduleFolderMapping ||
    editableModules.value.length === 0
  ) {
    return null;
  }

  if (props.validConfigurations && props.validConfigurations.length > 0) {
    const currentConfig = editableModules.value
      .map((module) => {
        const moduleData = props.moduleFolderMapping[module.id];
        const moduleType = moduleData?.moduleType;
        const moduleName = moduleData?.moduleName || module.name;

        // Distinguish between different links
        if (moduleType === 'Link') {
          if (moduleName.startsWith('I')) {
            return 'I';
          }
          if (moduleName.startsWith('L')) {
            return 'L';
          }
          return '?';
        }

        switch (moduleType) {
          case 'BaseDrive':
            return 'BD';
          case 'Drive':
            return 'D';
          case 'Tool':
            return 'E';
          default:
            return '?';
        }
      })
      .join(',');

    const isValidConfig = props.validConfigurations.some(
      (validConfig) => validConfig === currentConfig,
    );

    if (!isValidConfig) {
      return $gettext(
        'Invalid Robot Configuration. Check valid Configurations above',
      );
    }
  }

  const getModuleTypeById = (id: string) =>
    props.moduleFolderMapping[id]?.moduleType;

  const baseDriveIndices: number[] = [];
  const toolIndices: number[] = [];
  for (let i = 0; i < editableModules.value.length; i++) {
    const moduleType = getModuleTypeById(editableModules.value[i].id);
    if (moduleType === 'BaseDrive') {
      baseDriveIndices.push(i);
    } else if (moduleType === 'Tool') {
      toolIndices.push(i);
    }
  }

  if (baseDriveIndices.length === 0) {
    return $gettext(
      'You must add one BaseDrive module as first module (e.g. BD116)',
    );
  }
  if (baseDriveIndices.length > 1) {
    return $gettext('Only one BaseDrive is allowed and it must be first.');
  }
  if (baseDriveIndices[0] !== 0) {
    return $gettext('The first module must be a BaseDrive (e.g. BD116)');
  }
  if (toolIndices.length === 0) {
    return $gettext('You must add a Tool as the last module (e.g. E86, E116)');
  }
  if (toolIndices.length > 1) {
    return $gettext('Only one Tool is allowed and it must be last.');
  }
  if (toolIndices[0] !== editableModules.value.length - 1) {
    return $gettext('The last module must be a Tool (e.g. E86, E116)');
  }

  return null;
});

function removeModule(index: number) {
  emit('remove', index);
  if (props.validateConfig) {
    emit('validation-error', validationError.value);
  }
}

function getModuleName(module: ModuleIdNameMapping): string {
  if (props.moduleFolderMapping && props.moduleFolderMapping[module.id]) {
    return props.moduleFolderMapping[module.id].moduleName;
  }
  return module.name;
}

function onDragEnd() {
  // Emit validation after drag ends
  if (props.validateConfig) {
    emit('validation-error', validationError.value);
  }
  emit('update:modules', editableModules.value);
}

watch(
  () => props.modules,
  (newVal) => {
    editableModules.value = [...newVal];
    if (props.validateConfig) {
      emit('validation-error', validationError.value);
    }
  },
  { deep: true },
);

onMounted(() => {
  if (props.validateConfig) {
    emit('validation-error', validationError.value);
  }
});
</script>

<template>
  <div>
    <VueDraggableNext
      :list="editableModules"
      :animation="200"
      ghostClass="ghost"
      chosenClass="chosen"
      dragClass="drag"
      @end="onDragEnd"
    >
      <li
        v-for="(element, index) in editableModules"
        :key="element.id"
        class="module-list-item d-flex align-center justify-space-between px-2 py-1"
      >
        <div class="mr-2">
          <v-icon icon="mdi-drag" color="grey" size="small" />
        </div>
        <span class="flex-grow-1">{{ getModuleName(element) }}</span>
        <div class="ml-2">
          <v-icon
            icon="mdi-close"
            color="error"
            size="small"
            @click="removeModule(index)"
          />
        </div>
      </li>
    </VueDraggableNext>
  </div>
</template>

<style lang="scss" scoped>
.ghost {
  opacity: 0.5;
}

.drag {
  opacity: 0.5;
}

.chosen {
  opacity: 0.8;
  background: #f5f5f5;
}

.module-list-item {
  cursor: move;
  border-radius: 4px;
}
</style>
